import { NavLink } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { cn } from '@/utils/cn';
import type { NavItem } from '@/components/layout/Sidebar';

const ACCENT_ACTIVE: Record<'violet' | 'slate', string> = {
  violet:
    'bg-primary-50 text-primary-700 border-primary-500 dark:bg-primary-950/80 dark:text-primary-200 dark:border-primary-400',
  slate:
    'bg-slate-100 text-slate-900 border-slate-400 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-500',
};

export function NavLinkRow({
  item,
  collapsed,
  locked,
  accent,
  inFlyout = false,
  iconOverride,
}: {
  item: NavItem;
  collapsed: boolean;
  locked?: boolean;
  accent: 'violet' | 'slate';
  inFlyout?: boolean;
  iconOverride?: SvgIconComponent;
}) {
  const Icon = iconOverride ?? item.icon;
  const content = (
    <>
      <Icon className="shrink-0 opacity-90" fontSize="small" />
      {(!collapsed || inFlyout) && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {locked && <LockOutlinedIcon className="shrink-0 opacity-50" sx={{ fontSize: 14 }} />}
        </>
      )}
    </>
  );

  if (locked) {
    return (
      <span
        className={cn(
          'workspace-nav-item workspace-nav-item--locked',
          collapsed && !inFlyout && 'justify-center !px-2',
          inFlyout && 'workspace-nav-item--flyout',
        )}
        title={collapsed && !inFlyout ? `${item.label} (upgrade required)` : 'Upgrade your plan to unlock'}
      >
        {content}
      </span>
    );
  }

  const activeClass = ACCENT_ACTIVE[accent];

  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'workspace-nav-item',
          inFlyout && 'workspace-nav-item--flyout',
          isActive && cn('workspace-nav-item--active border-l-2', activeClass),
          collapsed && !inFlyout && 'justify-center !px-2',
        )
      }
      title={collapsed && !inFlyout ? item.label : undefined}
    >
      {content}
    </NavLink>
  );
}
